import {
  Fragment,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  Suspense,
  lazy,
} from "react";
import {
  NavLink,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import AdminContentLayout from "../layouts/AdminContentLayout";
import ActiveLabelsTable from "../components/Tables/ActiveLabelsTable";
import PickupTable from "../components/Tables/PickupTable";
import CustomPopUp from "../components/global/CustomPopup";
import GenerateLabel from "../components/organism/GenerateLabel";
import ErrorBoundary from "../components/global/ErrorBoundary";
import TableLoadingFallback from "../components/global/LoadingIndicators/TableLoadingFallback";
import Button from "../components/organism/Button";
import CustomText from "../components/atomic/CustomText";
import InfoSliderDispatcher from "../components/molecular/InfoSliderDispatcherButton";
import selectedRowsState from "../services/context/selectedRowsIDContext";
import { ApplicationState } from "../services/context/ApplicationStateContext";
import SchedulePickup from "./SchedulePickup";
import useProfile from "../services/utils/custom hooks/useProfile";
import useFeatureFlags from "../services/utils/custom hooks/Feature Flagging/useFeatureFlags";
import dataConstants from "../constants/dataConstants";

// Lazy load heavy table component
const TestOrdersTable = lazy(
  () => import("../components/Tables/TestOrdersTable")
);

function ShipmentsPage(): JSX.Element {
  const { path, url } = useRouteMatch();
  const [selectedRows, setSelectedRows] = useState([]);
  const { isSectionActive } = useFeatureFlags();
  const location = useLocation();
  const history = useHistory();
  const [selectedRowIds, setSelectedRowIds] = useState<any>([]);
  const [popupState, setPopupState] = useState({
    showLabelPopup: false,
    associateExistingLabelPopup: false,
    orgPopup: false,
  });

  const { data: profile }: any = useProfile();
  const roles = profile?.user?.roles ?? [];
  const isPLUOnly = useMemo(() => roles.includes("PathLab User"), [roles]);

  const {
    state: { selectedOrganization, selectedPathLab },
  } = useContext(ApplicationState);
  const { state, dispatch: dispatchRows } = useContext(selectedRowsState);
  const { viewOrderPopup } = state;
  const togglePopupState = (popup: string, value: boolean) =>
    setPopupState((prev) => ({ ...prev, [popup]: value }));

  const handleSelectedRowIdsChange = useCallback(
    (newSelectedRowIds: any) => {
      setSelectedRowIds(newSelectedRowIds);
      dispatchRows({
        type: "SET_SELECTED_ROWS_IDS",
        payload: newSelectedRowIds,
      });
    },
    [dispatchRows]
  );

  const handlePopupClose = useCallback(() => {
    togglePopupState("orgPopup", false);
    history.replace("/access/dashboard");
  }, [history]);

  useEffect(() => {
    if (!isSectionActive(dataConstants.FeatureFlagSectionKeys.SHIPMENTS)) {
      history.push("/unauthorized");
    }
  }, [isSectionActive]);

  useEffect(() => {
    if (
      location?.pathname === "/access/shipments" &&
      !popupState.associateExistingLabelPopup
    ) {
      history.push("/access/shipments/unshipped-orders");
    }
  }, [location, history, popupState.associateExistingLabelPopup]);

  const renderPopup = useCallback(
    (condition: any, hideFn: any, title: string, content: any) => {
      return (
        condition && (
          <CustomPopUp
            hidePopUp={hideFn}
            popUpTitle={title}
            onlineOrder={false}
          >
            {content}
          </CustomPopUp>
        )
      );
    },
    []
  );

  const actionButtons = useMemo(() => {
    if (location?.pathname === "/access/shipments/unshipped-orders") {
      return [
        {
          buttonTheme: "secondary",
          textInformation: {
            text: "Associate existing label",
            fontWeight: "font-semibold",
          },
          onClick: () => togglePopupState("associateExistingLabelPopup", true),
          type: "submit",
          disabled: selectedRows.length < 1,
          className: "mr-6",
        },
        {
          buttonTheme: "primary",
          textInformation: {
            text: "Create label",
            fontWeight: "font-semibold",
          },
          onClick: () => togglePopupState("showLabelPopup", true),
          type: "submit",
          className: "mr-6",
          disabled: selectedRows.length < 1 || !selectedOrganization,
          tooltipText: "Please select an organization first.",
          showTooltip: !selectedOrganization && true,
        },
      ];
    }
    if (location?.pathname === "/access/shipments/pick-ups") {
      return [
        {
          buttonTheme: "primary",
          textInformation: {
            text: "Schedule a pickup",
            fontWeight: "font-semibold",
          },
          onClick: () => history.push("/access/schedule-pickup"),
          type: "submit",
          className: "mr-6",
          disabled: !selectedOrganization,
          tooltipText: "Please select an organization first.",
          showTooltip: !selectedOrganization && true,
        },
      ];
    }
    return "";
  }, [location, selectedRows, history, selectedOrganization]);

  return (
    <Fragment>
      {/* Unshipped orders associate existing label header popup */}
      {renderPopup(
        popupState.associateExistingLabelPopup,
        () => togglePopupState("associateExistingLabelPopup", false),
        "",
        <ActiveLabelsTable
          tableContainerClass="md:w-full xs:w-[80vw]"
          selectedRowIds={selectedRowIds}
        />
      )}

      {/* Active labels view click popup */}
      {renderPopup(
        viewOrderPopup,
        () => dispatchRows({ type: "SET_VIEW_ORDER_POPUP", payload: false }),
        "Associated Orders",
        <Suspense fallback={<TableLoadingFallback />}>
          <TestOrdersTable
            tableContainerClass="h-[20vh] md:w-full xs:w-[80vw] "
            selectedRows={state.selectedRowsID}
          />
        </Suspense>
      )}

      {/* If an organization isn't selected popup */}
      {!selectedOrganization &&
        !isPLUOnly &&
        renderPopup(
          popupState.orgPopup,
          handlePopupClose,
          "Shipments",
          <Fragment>
            <CustomText
              text="Please select an organization before proceeding"
              fontWeight="font-normal"
              className="text-red-500"
            />
            <div className="flex items-end justify-end mt-3">
              <Button
                buttonTheme="primary"
                textInformation={{ text: `OK`, fontWeight: "font-semibold" }}
                onClick={handlePopupClose}
              />
            </div>
          </Fragment>
        )}

      {/* If a pathlab isn't selected popup */}
      {!selectedPathLab &&
        isPLUOnly &&
        renderPopup(
          popupState.orgPopup,
          handlePopupClose,
          "Shipments",
          <Fragment>
            <CustomText
              text="Please select a PathLab before proceeding"
              fontWeight="font-normal"
              className="text-red-500"
            />
            <div className="flex items-end justify-end mt-3">
              <Button
                buttonTheme="primary"
                textInformation={{ text: `OK`, fontWeight: "font-semibold" }}
                onClick={handlePopupClose}
              />
            </div>
          </Fragment>
        )}

      {/* Generate label popup */}
      {renderPopup(
        popupState.showLabelPopup,
        () => togglePopupState("showLabelPopup", false),
        "Create Label",
        <GenerateLabel
          selectedRowIds={selectedRowIds}
          setLabelPopup={() => togglePopupState("showLabelPopup", false)}
        />
      )}

      <AdminContentLayout
        pageTitle="Shipments | CSTL"
        subHeaderTitle="Shipments"
        customButtons={actionButtons}
      >
        <div className="overflow-auto">
          {location?.pathname !== "/access/shipments/schedule-pickup" && (
            <nav className="bg-sideNavGray rounded-lg h-[51px] mx-3 md:mx-5 my-6 flex md:w-[425px] justify-center items-center border border-roundedInputBorder">
              {["unshipped-orders", "active-labels", "pick-ups"].map((tab) => (
                <NavLink
                  key={tab}
                  to={`${url}/${tab}`}
                  className="shipmentTab capitalize"
                  activeClassName="shipmentActiveTab"
                >
                  {tab.replace("-", " ")}
                </NavLink>
              ))}
            </nav>
          )}

          <Switch>
      <Route
  exact
  path={`${path}/unshipped-orders`}
  render={() => (
    <ErrorBoundary>
      <div className="px-3 md:px-5">
        <div className="flex flex-col md:flex-row justify-start items-start md:items-center -mb-4">
          <div className="flex items-center">
            <CustomText
              text="Unshipped Orders"
              fontWeight="font-semibold"
              fontSize="text-md"
              className=""
            />
            <InfoSliderDispatcher
              infoQuery={dataConstants.infoSliderQuery.unshipped}
            />
          </div>

          {/* New Mobile-Only Button Container */}
          <div className="flex flex-wrap md:hidden my-4 gap-2 sticky top-0">
            {Array.isArray(actionButtons) && actionButtons.map((btn:any, idx:any) => (
              <Button
                key={idx}
                buttonTheme={btn.buttonTheme}
                textInformation={btn.textInformation}
                onClick={btn.onClick}
                type={btn.type}
                disabled={btn.disabled}
                tooltipText={btn.tooltipText}
                showTooltip={btn.showTooltip}
                // Ensure they look good on mobile
                className="w-full xs:w-auto text-[12px] px-3" 
              />
            ))}
          </div>
        </div>

        <Suspense fallback={<TableLoadingFallback />}>
          <TestOrdersTable
            setSelectedRows={setSelectedRows}
            onSelectedRowIdsChange={handleSelectedRowIdsChange}
          />
        </Suspense>
      </div>
    </ErrorBoundary>
  )}
/>
            <Route
              path={`${path}/schedule-pickup`}
              component={SchedulePickup}
            />
            <Route
              path={`${path}/active-labels`}
              component={ActiveLabelsTable}
            />
            <Route path={`${path}/pick-ups`} component={PickupTable} />
          </Switch>
        </div>
      </AdminContentLayout>
    </Fragment>
  );
}

export default ShipmentsPage;
